import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Fetching, Form, FormRow, Label, PulseIndicator, QueryDropdown } from 'basic-components';
import { Add } from 'basic-components/icons';
import { DropdownService, globalActions } from 'primary-components';

import { creatorCurvesActions } from 'actions/curves';
import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { curvesHooks } from 'hooks';
import properties from 'resources/constants/properties.json';
import { CurvesUtils } from 'utils';

import AddCurveDatapointModal from '../../AddCurveDatapointModal';
import CurveDropdown from './CurveDropdown';

import { useQueryClient } from '@tanstack/react-query';
import { When } from 'react-if';

import './CurveCreator.scss';

const EMPTY_ARRAY = []
const emptyObject = {}

const CurveCreator = () => {
  const dispatch = useDispatch(),
        curveValues = useSelector(state => state.curvesState.creatorState.curveValues) || emptyObject,
        groupName = useSelector(state => state.curvesState.creatorState.groupName),
        root = useSelector(state => state.curvesState.creatorState.root),
        isBasketOpen = useSelector(state => state.curvesState.creatorState.isBasketOpen),
        selectedCurves = useSelector(state => state.curvesState.creatorState.selectedCurves) || EMPTY_ARRAY,
        source = useSelector(state => state.globalState.localSource),
        sources = useSelector(state => state.globalState.sources) || EMPTY_ARRAY,
        localSource = useMemo(() => sources.find(p => p.producerId === 0)?.name, [sources]),
        [isAddCurveDatapointModalOpen, setIsAddCurveDatapointModalOpen] = useState(false),
        [isConfigLoading, setIsConfigLoading] = useState(true),
        [curveSourceConfig, setCurveSourceConfig] = useState(),
        swapIsOpen = useCallback(() => setIsAddCurveDatapointModalOpen(open => !open), []),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        getEnvCurves = curvesHooks.useEnvCurves({ source, localSource }),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        dropdownRefs = useRef([]),
        getDropdownRef = useCallback(index => {
          if(!dropdownRefs.current[index]) {
            dropdownRefs.current[index] = { current: null }
          }

          return dropdownRefs.current[index]
        }, []),
        { fetchNextPage: fetchMoreGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = EMPTY_ARRAY } = DropdownService.useQueryGroups({
          queryClient,
          input: groupQuery,
          getApiConfig: getApiConfigLocal,
          symbolsOnly: true,
          extraQuery: "fields.name=Forward_Curve",
          provider: source,
          useV2: true
        }),
        onChangeRedux = useCallback((val, { name }) => dispatch(creatorCurvesActions.onChange(val, name)), [dispatch]),
        onChangeRoot = useCallback(val => onChangeRedux(val, { name: "root" }), [onChangeRedux]),
        curveConfig = useMemo(() => curveSourceConfig?.symbols || EMPTY_ARRAY, [curveSourceConfig]),
        fixedGroupName = useMemo(() => curveSourceConfig?.groupName, [curveSourceConfig]),
        effectiveGroupName = useMemo(() => fixedGroupName || groupName, [fixedGroupName, groupName]),
        focusDropdown = useCallback(index => {
          setTimeout(() => dropdownRefs.current[index]?.dropdown?.current?.focus?.(), 0)
        }, []),
        focusRoot = useCallback(() => {
          setTimeout(() => rootRef.current?.dropdown?.current?.focus?.(), 0)
        }, []),
        onChangeCurveValue = useCallback((val, { index }) => {
          const nextCurveValues = curveConfig.slice(0, index + 1).reduce((acc, currentConfig, currentIndex) => {
            if(currentIndex === index) {
              acc[currentConfig.filterKey] = val
            } else if(curveValues[currentConfig.filterKey] !== undefined) {
              acc[currentConfig.filterKey] = curveValues[currentConfig.filterKey]
            }
            return acc
          }, {})

          dispatch(creatorCurvesActions.onChange(nextCurveValues, "curveValues"))
          dispatch(creatorCurvesActions.onChange(undefined, "root"))
          if(curveConfig[index + 1]) {
            focusDropdown(index + 1)
          } else {
            focusRoot()
          }
        }, [curveConfig, curveValues, dispatch, focusDropdown, focusRoot]),
        onChangeGroupName = useCallback(val => {
          dispatch(creatorCurvesActions.onChange({}, "curveValues"))
          dispatch(creatorCurvesActions.onChange(undefined, "root"))
          if(fixedGroupName) {
            dispatch(creatorCurvesActions.onChange(fixedGroupName, "groupName"))
          } else {
            onChangeRedux(val, { name: "groupName" })
          }
          if(curveConfig[0]) {
            focusDropdown(0)
          } else {
            focusRoot()
          }
        }, [curveConfig, dispatch, fixedGroupName, focusDropdown, focusRoot, onChangeRedux]),
        rootMetadatas = useMemo(() => curveConfig.reduce((acc, config) => {
          const stateKey = config.filterKey
          const value = curveValues[stateKey]
          if(value) {
            acc[config.filterKey] = [value]
          }
          return acc
        }, {}), [curveConfig, curveValues]),
        rootReady = useMemo(() => curveConfig.length > 0 && !!effectiveGroupName && curveConfig.every(config => !!curveValues[config.filterKey]), [curveConfig, curveValues, effectiveGroupName]),
        [rootValueQuery, setRootValueQuery] = useState(),
        { fetchNextPage: fetchMoreRootValues, isFetching: isFetchingRootValues, isFetchingNextPage: isFetchingNextPageRootValues, data: rootValues = EMPTY_ARRAY } = DropdownService.useQuerySymbolValues({
          queryClient,
          input: rootValueQuery,
          key: CurvesUtils.ROOT,
          groupName: effectiveGroupName,
          metadata: true,
          metadatas: rootMetadatas,
          useEs: properties.useEsSymbolListing === "true",
          getApiConfig: getApiConfigLocal,
          enabled: rootReady,
          provider: source,
          useV2: true
        }),
        addItem = useCallback(() => {
          dispatch(creatorCurvesActions.addCurve({
            groupName: effectiveGroupName,
            root,
            source
          }))
          onChangeGroupName(undefined)
        }, [dispatch, effectiveGroupName, onChangeGroupName, root, source]),
        addItemWithToast = useCallback(() => {
          dispatch(globalActions.getToastsStore()).warning(creatorSeriesActions.PROVIDER_MESSAGE, "Discard selection", {
            withCancel: false,
            confirmText: "Discard all",
            confirmAction: () => {
              dispatch(creatorCurvesActions.onChange([], "selectedCurves"))
              addItem()
            }
          })
        }, [addItem, dispatch]),
        onAddItem = useCallback(() => {
          if(selectedCurves?.length > 0 && selectedCurves[0]?.source !== source) {
            addItemWithToast()
          } else {
            addItem()
          }
        }, [addItem, addItemWithToast, selectedCurves, source]),
        groupRef = useRef(),
        rootRef = useRef()

  useEffect(() => {
    setCurveSourceConfig(undefined)
  }, [source])

  useEffect(() => {
    if(curveSourceConfig !== undefined) {
      return;
    }

    setIsConfigLoading(true)
    getEnvCurves().then(sourceConfig => {
      setCurveSourceConfig(sourceConfig)
      dispatch(creatorCurvesActions.onChange({}, "curveValues"))
      if(sourceConfig?.groupName) {
        dispatch(creatorCurvesActions.onChange(sourceConfig.groupName, "groupName"))
      } else {
        dispatch(creatorCurvesActions.onChange(undefined, "groupName"))
      }
      dispatch(creatorCurvesActions.onChange(undefined, "root"))
    }).finally(() => setIsConfigLoading(false))
  }, [curveSourceConfig, dispatch, getEnvCurves])

  useEffect(() => {
    rootRef.current?.refreshOptions?.()
  }, [rootMetadatas])

  useEffect(() => {
    if(root === undefined) {
      rootRef.current?.dropdown?.current?.setValue(root)
    }
  }, [root])

  useEffect(() => {
    if(groupName === undefined) {
      groupRef.current?.dropdown?.current?.setValue(groupName)
    }
  }, [groupName])

  return (
    <Fragment>
      {isConfigLoading ? (
        <div className="ng-office-app__authed__content__body__item__curve-creator__loading">
          <Fetching
              loading
              size={100}
          />
          <div className="ng-office-app__authed__content__body__item__curve-creator__loading__label">
            <p>Loading</p>
            <PulseIndicator
                color="grey-dark"
                loading
                size={4}
                speedMultiplier={.5}
            />
          </div>
        </div>
      ) : (
        <Form className="ng-office-app__authed__content__body__item__curve-creator">
        {!fixedGroupName && (
          <FormRow
              className="ng-office-app__authed__content__body__item__curve-creator__row"
              isVertical
          >
            <Label id="group">GROUP</Label>
            <QueryDropdown
                autoFocus={!groupName && !isBasketOpen}
                defaultValue={groupName}
                fetchNextPage={fetchMoreGroups}
                isFetching={isFetchingGroups || isFetchingNextPageGroups}
                labelId="group"
                menuPlacement="bottom"
                menuPortalTarget={document.querySelector('.ng-office-app')}
                name="groupName"
                noError
                onChange={onChangeGroupName}
                onInputChange={setGroupQuery}
                openMenuOnFocus
                options={groups}
                placeholder="Choose source group"
                ref={groupRef}
                variant="border"
            />
          </FormRow>
        )}
        {curveConfig.map((config, index) => {
          const stateKey = config.filterKey
          const priorMetadatas = curveConfig.slice(0, index).reduce((acc, priorConfig) => {
            const priorStateKey = priorConfig.filterKey
            const priorValue = curveValues[priorStateKey]
            if(priorValue) {
              acc[priorConfig.filterKey] = [priorValue]
            }
            return acc
          }, {})
          const priorMetadatasKey = Object.entries(priorMetadatas).map(([key, values]) => `${key}:${values.join(',')}`).join('|')
          const enabled = !!effectiveGroupName && curveConfig.slice(0, index).every(priorConfig => !!curveValues[priorConfig.filterKey])

          return (
            <CurveDropdown
                autoFocus={fixedGroupName ? index === 0 : false}
                config={config}
                defaultValue={curveValues[stateKey]}
                dropdownRef={getDropdownRef(index)}
                enabled={enabled}
                getApiConfigLocal={getApiConfigLocal}
                groupName={effectiveGroupName}
                index={index}
                key={`${config.filterKey}-${index}`}
                onChange={onChangeCurveValue}
                priorMetadatas={priorMetadatas}
                priorMetadatasKey={priorMetadatasKey}
                queryClient={queryClient}
                source={source}
                stateKey={stateKey}
            />
          )
        })}
        <FormRow
            className="ng-office-app__authed__content__body__item__curve-creator__row"
            isVertical
        >
          <Label id="root">ROOT</Label>
          <QueryDropdown
              autoFocus={false}
              defaultValue={root}
              disabled={!rootReady}
              fetchNextPage={fetchMoreRootValues}
              fixed
              isFetching={isFetchingRootValues || isFetchingNextPageRootValues}
              labelId="root"
              menuPlacement="bottom"
              menuPortalTarget={document.querySelector('.ng-office-app')}
              name="root"
              noError
              onChange={onChangeRoot}
              onInputChange={setRootValueQuery}
              openMenuOnFocus
              options={rootValues}
              placeholder="Choose root"
              ref={rootRef}
              variant="border"
          />
        </FormRow>
        <div className="ng-office-app__authed__content__body__item__curve-creator__divider"/>
        <div className="ng-office-app__authed__content__body__item__curve-creator__submit">
          <Button
              disabled={!root || !effectiveGroupName || !rootReady}
              onClick={onAddItem}
              size="small"
              variant="primary"
          >
            <Add
                color={!root || !effectiveGroupName || !rootReady ? 'grey-dark' : 'white-base'}
                height={16}
                width={16}
            />
            <span>Add curve</span>
          </Button>
        </div>
        <div className="ng-office-app__authed__content__body__item__curve-creator__submit--datapoint">
          <Button
              disabled={!root || !effectiveGroupName || !rootReady}
              onClick={swapIsOpen}
              size="small"
              variant="text"
          >
            Add single data point
          </Button>
          <When condition={isAddCurveDatapointModalOpen}>
            {() => (
              <AddCurveDatapointModal
                  onClose={swapIsOpen}
              />
            )}
          </When>
        </div>
      </Form>
      )}
    </Fragment>
  )
}

export default CurveCreator
